import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import api from '../utils/api';

/**
 * Iter-197 — Outdated Bundle / SW Update Toast.
 *
 * Listens for `SW_UPDATE_AVAILABLE` messages from the service worker
 * (broadcast on `activate` of a newer version). Renders a soft amber
 * banner pinned to the bottom of the viewport with Refresh + Dismiss
 * actions.
 *
 * Spam prevention
 * ───────────────
 *   • Per-version dismissal stored in localStorage so we never re-toast
 *     for the same SW version the user has already dismissed.
 *   • One toast per page lifecycle — re-mounts won't show it again
 *     unless a NEW version arrives.
 *
 * Sensitive-route guard
 * ─────────────────────
 *   Auto-refresh (idle path) is suppressed whenever the user is in
 *   the middle of applying / paying / clocking-in / filling a form.
 *   The banner is still shown — it just never reloads behind their
 *   back. They have to click Refresh themselves.
 *
 * Telemetry
 * ─────────
 *   Posts to /api/client-events/sw-update for: toast_shown,
 *   refresh_accepted, refresh_dismissed, version_received. This is the
 *   forensic trail we use to debug future stale-cache incidents.
 */

const SENSITIVE_PATH_PATTERNS = [
  /\/talent\/(shifts|gigs)\/[^/]+/,           // gig detail / apply flow
  /\/one-tap-apply/,                           // direct apply deep links
  /\/checkout/,
  /\/payment/,
  /\/checkin/,
  /\/clock-?in/i,
  /\/clock-?out/i,
  /\/survey/,                                  // post-event survey
  /\/profile\/(idv|verify|verification)/i,    // ID verification mid-flow
];

const isOnSensitiveRoute = () => {
  const path = window.location.pathname || '';
  return SENSITIVE_PATH_PATTERNS.some((re) => re.test(path));
};

// Treat the page as "actively in use" if there's a focused form
// element OR the user typed/clicked in the last 30s.
const ACTIVITY_IDLE_MS = 30000;
const useUserActivity = () => {
  const lastInteractRef = useRef(Date.now());
  useEffect(() => {
    const bump = () => { lastInteractRef.current = Date.now(); };
    window.addEventListener('mousedown', bump, { passive: true });
    window.addEventListener('keydown', bump, { passive: true });
    window.addEventListener('touchstart', bump, { passive: true });
    return () => {
      window.removeEventListener('mousedown', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('touchstart', bump);
    };
  }, []);
  return useCallback(() => {
    const focused = document.activeElement;
    const isTyping = focused && /^(INPUT|TEXTAREA|SELECT)$/.test(focused.tagName || '');
    const idleFor = Date.now() - lastInteractRef.current;
    return !isTyping && idleFor > ACTIVITY_IDLE_MS;
  }, []);
};

const logEvent = (event_type, extra = {}) => {
  // Fire-and-forget — never block the UI on telemetry.
  try {
    api.post('/client-events/sw-update', {
      event_type,
      current_version: localStorage.getItem('sw_version_current') || null,
      detected_version: localStorage.getItem('sw_version_detected') || null,
      path: window.location.pathname,
      is_idle: extra.is_idle ?? null,
      note: extra.note || null,
    }).catch(() => { /* noop */ });
  } catch (_) { /* noop */ }
};

const UpdateAvailableToast = () => {
  const [show, setShow] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const isIdle = useUserActivity();
  const armedRef = useRef(false);

  // Wire SW message listener.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      const data = event.data || {};
      if (data.type === 'SW_VERSION') {
        if (data.version) localStorage.setItem('sw_version_current', data.version);
        logEvent('version_received');
        return;
      }
      if (data.type !== 'SW_UPDATE_AVAILABLE') return;
      const version = data.version || 'unknown';
      const dismissedVersion = localStorage.getItem('sw_update_dismissed_version');
      if (dismissedVersion === version) {
        // User has already explicitly waved this version off — don't nag.
        return;
      }
      localStorage.setItem('sw_version_detected', version);
      setNewVersion(version);
      setShow(true);
      armedRef.current = true;
      logEvent('toast_shown', { is_idle: isIdle() });
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    // Ask the active SW for its current version on mount so we know
    // what's running RIGHT NOW.
    try {
      navigator.serviceWorker.controller?.postMessage({ type: 'GET_VERSION' });
    } catch (_) { /* noop */ }

    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Idle auto-refresh path: if the toast has been visible for ≥60s
  // AND the user is idle AND not on a sensitive route, refresh silently.
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => {
      if (!armedRef.current) return;
      if (!isIdle()) return;
      if (isOnSensitiveRoute()) return;
      doRefresh({ auto: true });
    }, 60000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const doRefresh = useCallback((opts = {}) => {
    setRefreshing(true);
    logEvent('refresh_accepted', { is_idle: isIdle(), note: opts.auto ? 'auto-idle' : 'manual' });
    try {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        // The new (waiting) worker is reachable via registration; ask
        // any worker to skip waiting — pwa.js already wires
        // controllerchange→reload but in case the SW is already active
        // we just reload directly.
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg && reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }).catch(() => {});
      }
    } catch (_) { /* noop */ }
    // Small delay to let the SW message land before we force reload.
    setTimeout(() => window.location.reload(), 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => {
    setShow(false);
    armedRef.current = false;
    if (newVersion) {
      localStorage.setItem('sw_update_dismissed_version', newVersion);
    }
    logEvent('refresh_dismissed', { is_idle: isIdle() });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVersion]);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-1.5rem)] max-w-md"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sw-update-toast"
    >
      <div className="bg-gradient-to-r from-amber-50 to-sky-50 border border-amber-300 shadow-xl rounded-xl px-4 py-3 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">
            A new version of iEvntür is available
          </p>
          <p className="text-[11px] text-gray-600 mt-0.5">
            Refresh to get the latest fixes
            {newVersion ? <span className="ml-1 opacity-70">· build {newVersion}</span> : null}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => doRefresh()}
          disabled={refreshing}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold shrink-0 disabled:opacity-60"
          data-testid="sw-update-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600 shrink-0 -mr-1 -mt-1 p-1"
          aria-label="Dismiss update notice"
          data-testid="sw-update-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UpdateAvailableToast;
